import DateHolidays from "date-holidays";
import dayjs, { type Dayjs } from "dayjs";
import dayjsBusinessTime from "dayjs-business-time";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/es";

dayjs.extend(utc);
dayjs.extend(timezone);

dayjs.locale("es");

dayjs.extend(dayjsBusinessTime);

const hd = new DateHolidays("CO");
const currentYear = dayjs().tz("America/Bogota").year();
const years = [currentYear - 1, currentYear, currentYear + 1];

const getLastBusinessDayOfYear = (year: number): string => {
  let d = dayjs.tz(`${year}-12-31`, "America/Bogota");
  while (d.day() === 0 || d.day() === 6 || !!hd.isHoliday(d.toDate()))
    d = d.subtract(1, "day");
  return d.format("YYYY-MM-DD");
};

dayjs.setHolidays(
  Array.from(
    new Set([
      ...years.flatMap((year) =>
        hd.getHolidays(year).map((holiday) => holiday.date.slice(0, 10))
      ),
      ...years.map(getLastBusinessDayOfYear),
    ])
  )
);

export const formatUtcDatetimeToColombian = (utcDateString: string): string => {
  const timeZone = "America/Bogota";
  const colombianTime: Dayjs = dayjs.utc(utcDateString).tz(timeZone);

  return colombianTime.format("YYYY-MM-DD h:mm:ss a");
};

export const formatDatetime = (datetime: string): string => {
  return dayjs(datetime).locale("es").format("YYYY-MM-DD h:mm:ss a");
};

export const formatDate = (date: Date): string => {
  return dayjs(date).format("YYYY-MM-DD");
};

export const businessDayOfMonth = (month: Dayjs, day: number): Dayjs => {
  const nominal = month.date(day).startOf("day");

  return nominal.isBusinessDay() ? nominal : nominal.nextBusinessDay();
};

export const now = (): Dayjs => {
  return dayjs().tz("America/Bogota");
};

export const date = (
  date?: string | number | Date | Dayjs,
  customFormat?: string
): Dayjs => {
  try {
    if (!date) return dayjs(null);
    if (dayjs.isDayjs(date)) return date;

    // If the date is a Date object, parse and convert to Bogota
    if (date instanceof Date) return dayjs(date).tz("America/Bogota");

    // If the date has timezone info (number or string with Z/offset), parse and convert to Bogota
    const isNumberOrHasTimezoneInfo =
      typeof date === "number" || /(?:Z|[+-]\d{2}:?\d{2})$/.test(date);
    if (isNumberOrHasTimezoneInfo)
      return dayjs(date, customFormat).tz("America/Bogota");

    // No timezone info in datetime string -> interpret as Bogota timezone
    if (customFormat) return dayjs.tz(date, customFormat, "America/Bogota");
    else return dayjs.tz(date, "America/Bogota");
  } catch {
    // If the date is invalid, parse and convert to Bogota
    return dayjs(date, customFormat).tz("America/Bogota");
  }
};
